import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Location } from '../../types';
import { Hint } from '../UI/Bloom/flow';
import { T, rgba } from '../../utils/adminTheme';

interface DateSelectionProps {
  selectedLocation: Location;
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  isDateAvailable: (date: Date) => boolean;
}

export const DateSelection: React.FC<DateSelectionProps> = ({
  selectedLocation,
  selectedDate,
  onDateSelect,
  isDateAvailable,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Nächste 5 verfügbare Tage als Quick-Chips
  const quickDays = useMemo(() => {
    const result: { date: Date; label: string }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i <= 60 && result.length < 5; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      if (isDateAvailable(d)) {
        const diff = i;
        const label = diff === 0 ? 'Heute'
          : diff === 1 ? 'Morgen'
          : diff === 2 ? 'Übermorgen'
          : d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' });
        result.push({ date: d, label });
      }
    }
    return result;
  }, [isDateAvailable]);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    setTouchStart({ x: t.clientX, y: t.clientY });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const t = e.touches[0];
    const diffX = Math.abs(t.clientX - touchStart.x);
    const diffY = Math.abs(t.clientY - touchStart.y);
    if (diffX > diffY) e.preventDefault();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const diff = e.changedTouches[0].clientX - touchStart.x;
    if (diff > 50) setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    if (diff < -50) setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setTouchStart(null);
  };

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const adjustedFirstDay = (firstDay + 6) % 7;
  const today = new Date(new Date().setHours(0, 0, 0, 0));

  const days: React.ReactNode[] = [];
  for (let i = 0; i < adjustedFirstDay; i++) {
    days.push(<div key={`empty-${i}`} />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const isPast = date < today;
    const available = isDateAvailable(date) && !isPast;
    const isSelected = selectedDate?.toDateString() === date.toDateString();

    days.push(
      <button
        key={day}
        onClick={() => available && onDateSelect(date)}
        disabled={!available}
        style={{
          aspectRatio: '1 / 1',
          minHeight: 44,
          borderRadius: 999,
          border: `1px solid ${isSelected ? T.accent : available ? T.line : T.lineSoft}`,
          background: isSelected ? T.accent : available ? T.surface : rgba(T.faint, 0.08),
          color: isSelected ? '#fff' : available ? T.text : T.faint,
          fontFamily: T.body,
          fontSize: 14,
          fontWeight: isSelected ? 700 : 500,
          cursor: available ? 'pointer' : 'not-allowed',
          opacity: available ? 1 : 0.62,
          transition: 'all .16s',
        }}
      >
        {day}
      </button>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Quick-Chips: nächste verfügbare Tage */}
      {quickDays.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {quickDays.map(({ date, label }) => {
            const active = selectedDate?.toDateString() === date.toDateString();
            return (
              <button
                key={date.toISOString()}
                onClick={() => {
                  onDateSelect(date);
                  setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
                }}
                style={{
                  padding: '7px 14px',
                  borderRadius: 999,
                  border: `1px solid ${active ? T.accent : T.line}`,
                  background: active ? T.accent : T.surface,
                  color: active ? '#fff' : T.text,
                  fontFamily: T.body,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  transition: 'all .14s',
                  boxShadow: active ? `0 4px 14px ${rgba(T.accent, 0.22)}` : 'none',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        background: T.surface,
        border: `1px solid ${T.line}`,
        borderRadius: T.r.lg,
        padding: 18,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          style={navButtonStyle}
          aria-label="Vorheriger Monat"
        >
          <ChevronLeft size={18} />
        </button>

        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: T.body, fontSize: 18, fontWeight: 700, color: T.text,
            letterSpacing: '-0.01em',
          }}>
            {currentMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
          <div style={{ marginTop: 2, color: T.muted, fontFamily: T.body, fontSize: 11 }}>
            {selectedLocation.name}
          </div>
        </div>

        <button
          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          style={navButtonStyle}
          aria-label="Nächster Monat"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 7,
        marginBottom: 8,
        color: T.muted,
        fontFamily: T.body,
        fontSize: 12,
        textAlign: 'center',
      }}>
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
          <div key={day}>{day}</div>
        ))}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: 7,
      }}>
        {days}
      </div>

      <div style={{ marginTop: 18 }}>
        <Hint>
          Helle Tage sind verfügbar — Slots passen sich automatisch der Behandlungsdauer an.
        </Hint>
      </div>
    </div>
    </div>
  );
};

const navButtonStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 999,
  border: `1px solid ${T.line}`,
  background: T.surface2,
  color: T.text,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
